import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../utils/apiClient';
import './SetupAgentPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const SetupAgentPage = () => {
  const [mode, setMode] = useState('single'); // 'single' | 'bulk' | 'bulk-update'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [routingProfiles, setRoutingProfiles] = useState([]);
  const [loadingRoutingProfiles, setLoadingRoutingProfiles] = useState(false);
  const [securityProfiles, setSecurityProfiles] = useState([]);
  const [loadingSecurityProfiles, setLoadingSecurityProfiles] = useState(false);

  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  // Get user role from localStorage
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const userRole = user?.role || '';
  const isStandardUser = userRole.toUpperCase() === 'STANDARD';

  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const SINGLE_AGENT_API = apiEndpoints.agentApi;
  const BULK_AGENT_API = apiEndpoints.bulkAgentApi;
  const BULK_AGENT_PRESIGN_API = apiEndpoints.bulkAgentPresignApi;
  const BULK_AGENT_UPDATE_API = apiEndpoints.bulkAgentUpdateApi;
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;
  const ROUTING_PROFILES_API = apiEndpoints.routingProfiles;
  const SECURITY_PROFILES_API = apiEndpoints.securityProfiles;

  // Single agent form state
  const [singleForm, setSingleForm] = useState({
    instance_id: '',
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    routing_profile_id: '',
    security_profile_ids: [],
    phone_type: 'SOFT_PHONE',
    desk_phone_number: ''
  });

  // Bulk form state
  const [bulkForm, setBulkForm] = useState({
    instance_id: '',
    csv_file: null
  });
  const [csvFileName, setCsvFileName] = useState('');
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);

  // Bulk update form state
  const [updateForm, setUpdateForm] = useState({
    instance_id: '',
    csv_file: null
  });
  const [updateCsvFileName, setUpdateCsvFileName] = useState('');
  const [updateCsvValidationErrors, setUpdateCsvValidationErrors] = useState([]);

  // Submitted bulk job ID (shown after successful submit)
  const [submittedJobId, setSubmittedJobId] = useState(null);

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    
    // Clear all dropdowns and form data when environment changes
    setInstances([]);
    setRoutingProfiles([]);
    setSecurityProfiles([]);
    setSingleForm({
      instance_id: '',
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      routing_profile_id: '',
      security_profile_ids: [],
      phone_type: 'SOFT_PHONE',
      desk_phone_number: ''
    });
    setBulkForm({
      instance_id: '',
      csv_file: null
    });
    setCsvFileName('');
    setCsvValidationErrors([]);
    setUpdateForm({
      instance_id: '',
      csv_file: null
    });
    setUpdateCsvFileName('');
    setUpdateCsvValidationErrors([]);
    setResult(null);
    setError(null);
    setSubmittedJobId(null);
  };

  const handleSingleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Handle multi-select for security profiles
    if (name === 'security_profile_ids') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setSingleForm(prev => ({ ...prev, [name]: selectedOptions }));
    } else {
      setSingleForm(prev => ({ ...prev, [name]: value }));
      
      // If instance_id changes, fetch routing and security profiles
      if (name === 'instance_id' && value) {
        fetchRoutingProfiles(value);
        fetchSecurityProfiles(value);
      }
    }
  };

  const handleBulkFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'instance_id') {
      setBulkForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdateFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'instance_id') {
      setUpdateForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setUpdateCsvFileName('');
        setUpdateForm(prev => ({ ...prev, csv_file: null }));
        return;
      }
      setUpdateCsvFileName(file.name);
      setUpdateForm(prev => ({ ...prev, csv_file: file }));
      setUpdateCsvValidationErrors([]);
      setError(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setCsvFileName('');
        setBulkForm(prev => ({ ...prev, csv_file: null }));
        return;
      }
      
      setCsvFileName(file.name);
      setBulkForm(prev => ({ ...prev, csv_file: file }));
      setCsvValidationErrors([]);
      setError(null);
    }
  };

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiClient.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  // Fetch routing profiles for a specific instance
  const fetchRoutingProfiles = async (instanceId) => {
    setLoadingRoutingProfiles(true);
    setRoutingProfiles([]);
    setSingleForm(prev => ({ ...prev, routing_profile_id: '' }));
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiClient.post(ROUTING_PROFILES_API, {
        instance_id: instanceId,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setRoutingProfiles(response.data.routing_profiles || []);
      } else {
        console.error('Failed to load routing profiles:', response.data.message);
        setError('Failed to load routing profiles');
      }
    } catch (err) {
      console.error('Error fetching routing profiles:', err);
      setError('Error fetching routing profiles: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingRoutingProfiles(false);
    }
  };

  // Fetch security profiles for a specific instance
  const fetchSecurityProfiles = async (instanceId) => {
    setLoadingSecurityProfiles(true);
    setSecurityProfiles([]);
    setSingleForm(prev => ({ ...prev, security_profile_ids: [] }));
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiClient.post(SECURITY_PROFILES_API, {
        instance_id: instanceId,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setSecurityProfiles(response.data.security_profiles || []);
      } else {
        console.error('Failed to load security profiles:', response.data.message);
        setError('Failed to load security profiles');
      }
    } catch (err) {
      console.error('Error fetching security profiles:', err);
      setError('Error fetching security profiles: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingSecurityProfiles(false);
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        instance_id: singleForm.instance_id,
        agent: {
          Username: singleForm.username,
          FirstName: singleForm.first_name,
          LastName: singleForm.last_name,
          Email: singleForm.email || undefined,
          PhoneNumber: singleForm.phone_number || undefined,
          RoutingProfileId: singleForm.routing_profile_id,
          SecurityProfileIds: Array.isArray(singleForm.security_profile_ids) 
            ? singleForm.security_profile_ids.filter(id => id)
            : [],
          PhoneType: singleForm.phone_type
        }
      };

      // Add desk phone number if needed
      if (singleForm.phone_type === 'DESK_PHONE' && singleForm.desk_phone_number) {
        payload.agent.DeskPhoneNumber = singleForm.desk_phone_number;
      }

      // Clean up undefined values
      Object.keys(payload.agent).forEach(key => {
        if (payload.agent[key] === undefined) {
          delete payload.agent[key];
        }
      });

      const response = await apiClient.post(SINGLE_AGENT_API, payload);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setCsvValidationErrors([]);
    setSubmittedJobId(null);

    try {
      // Step 1: Get a pre-signed S3 PUT URL (small JSON → passes WAF body-size check)
      const presignResp = await apiClient.get(
        `${BULK_AGENT_PRESIGN_API}?instance_id=${encodeURIComponent(bulkForm.instance_id)}`
      );
      const { presign_url, s3_key, job_id } = presignResp.data;

      // Step 2: PUT the CSV file directly to S3 via the pre-signed URL
      // No auth header — the pre-signed URL itself carries the credentials
      await axios.put(presign_url, bulkForm.csv_file, {
        headers: { 'Content-Type': 'text/csv' },
        // Prevent axios from serialising the File object or adding extra headers
        transformRequest: [(data) => data],
      });

      // Step 3: Notify the backend that the file is in S3 and kick off the job
      const response = await apiClient.post(BULK_AGENT_API, {
        job_id,
        s3_key,
        instance_id: bulkForm.instance_id,
      });

      // Parse response body (Lambda Proxy Integration may wrap in body string)
      let bodyData = response.data;
      if (bodyData.body && typeof bodyData.body === 'string') {
        try { bodyData = JSON.parse(bodyData.body); } catch (_) {}
      }

      if (bodyData.errors && Array.isArray(bodyData.errors)) {
        // Structural/format validation failed — show errors immediately
        setCsvValidationErrors(bodyData.errors);
        setError(bodyData.message || 'CSV validation failed');
        return;
      }

      if (!bodyData.job_id) {
        setError(bodyData.message || bodyData.error || 'Unexpected response from server');
        return;
      }

      // 202 Accepted — job submitted; show the job ID and a link to the jobs screen
      setSubmittedJobId(bodyData.job_id);

    } catch (err) {
      console.error('Bulk upload error:', err);

      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.body && typeof errorData.body === 'string') {
          try {
            const bodyData = JSON.parse(errorData.body);
            if (bodyData.errors && Array.isArray(bodyData.errors)) {
              setCsvValidationErrors(bodyData.errors);
            }
            setError(bodyData.message || bodyData.error || 'Failed to upload CSV');
          } catch (_) {
            setError(errorData.body || 'Failed to upload CSV');
          }
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          setCsvValidationErrors(errorData.errors);
          setError(errorData.message || 'CSV validation failed');
        } else {
          setError(errorData.message || errorData.error || 'Failed to upload CSV');
        }
      } else {
        setError(err.message || 'Failed to process bulk upload');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setUpdateCsvValidationErrors([]);
    setSubmittedJobId(null);

    try {
      // Step 1: Get a pre-signed S3 PUT URL (reuse presign endpoint with type=agent-update)
      const presignResp = await apiClient.get(
        `${BULK_AGENT_PRESIGN_API}?type=agent-update&instance_id=${encodeURIComponent(updateForm.instance_id)}`
      );
      const { presign_url, s3_key, job_id } = presignResp.data;

      // Step 2: PUT the CSV directly to S3 via the pre-signed URL
      await axios.put(presign_url, updateForm.csv_file, {
        headers: { 'Content-Type': 'text/csv' },
        transformRequest: [(data) => data],
      });

      // Step 3: Notify backend to create job and enqueue update
      const response = await apiClient.post(BULK_AGENT_UPDATE_API, {
        job_id,
        s3_key,
        instance_id: updateForm.instance_id,
      });

      let bodyData = response.data;
      if (bodyData.body && typeof bodyData.body === 'string') {
        try { bodyData = JSON.parse(bodyData.body); } catch (_) {}
      }

      if (bodyData.errors && Array.isArray(bodyData.errors)) {
        setUpdateCsvValidationErrors(bodyData.errors);
        setError(bodyData.message || 'CSV validation failed');
        return;
      }

      if (!bodyData.job_id) {
        setError(bodyData.message || bodyData.error || 'Unexpected response from server');
        return;
      }

      setSubmittedJobId(bodyData.job_id);

    } catch (err) {
      console.error('Bulk update error:', err);

      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.body && typeof errorData.body === 'string') {
          try {
            const bodyData = JSON.parse(errorData.body);
            if (bodyData.errors && Array.isArray(bodyData.errors)) {
              setUpdateCsvValidationErrors(bodyData.errors);
            }
            setError(bodyData.message || bodyData.error || 'Failed to upload update CSV');
          } catch (_) {
            setError(errorData.body || 'Failed to upload update CSV');
          }
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          setUpdateCsvValidationErrors(errorData.errors);
          setError(errorData.message || 'CSV validation failed');
        } else {
          setError(errorData.message || errorData.error || 'Failed to upload update CSV');
        }
      } else {
        setError(err.message || 'Failed to process bulk update');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Setup Agent</h1>
          <p className="page-subtitle">Create Amazon Connect agents individually or in bulk</p>
        </div>
        <Link to="/home" className="btn btn-secondary">
          ← Back to Home
        </Link>
      </div>

      {/* Environment Selector */}
      <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
        >
          {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
        </select>
        <span style={{
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
          {selectedEnvironment}
        </span>
      </div>

      {/* <div className="alert" style={{ 
        backgroundColor: '#fee', 
        border: '1px solid #fcc', 
        borderRadius: '4px', 
        padding: '12px 16px', 
        marginBottom: '20px',
        color: '#c00',
        display: 'grid'
      }}>
        <strong>Note:</strong> All the users being onboarded using this application should be registered with Okta for the following roles:
        <ul style={{ margin: '2px 0 0 0', paddingLeft: '20px', listStyleType: 'disc', textAlign: 'left' }}>
          <li style={{ marginBottom: '4px' }}>O_AMAZONCONNECT_USERS</li>
          <li style={{ marginBottom: '4px' }}>O_AMAZONCONNECT_PVS</li>
          <li style={{ marginBottom: '4px' }}>O_AMAZONCONNECT_DEV</li>
        </ul>
      </div> */}

      <div className="mode-tabs">
        <button
          className={`tab-button ${mode === 'single' ? 'active' : ''}`}
          onClick={() => setMode('single')}
        >
          Single Agent
        </button>
        <button
          className={`tab-button ${mode === 'bulk' ? 'active' : ''}`}
          onClick={() => setMode('bulk')}
          disabled={isStandardUser}
          title={isStandardUser ? 'Bulk upload is not available for STANDARD users' : ''}
          style={isStandardUser ? { 
            opacity: 0.5, 
            cursor: 'not-allowed',
            backgroundColor: '#e0e0e0'
          } : {}}
        >
          Bulk Upload (CSV)
        </button>
        <button
          className={`tab-button ${mode === 'bulk-update' ? 'active' : ''}`}
          onClick={() => setMode('bulk-update')}
          disabled={isStandardUser}
          title={isStandardUser ? 'Bulk update is not available for STANDARD users' : ''}
          style={isStandardUser ? {
            opacity: 0.5,
            cursor: 'not-allowed',
            backgroundColor: '#e0e0e0'
          } : {}}
        >
          Bulk Update (CSV)
        </button>
      </div>

      {mode === 'single' && (
        <div className="form-container">
          <form onSubmit={handleSingleSubmit} className="setup-form">
            <div className="form-section">
              <h3>Instance Information</h3>
              <div className="form-group">
                <label htmlFor="instance_id">Instance *</label>
                <select
                  id="instance_id"
                  name="instance_id"
                  value={singleForm.instance_id}
                  onChange={handleSingleFormChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
              </div>
            </div>

            <div className="form-section">
              <h3>Agent Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={singleForm.username}
                    onChange={handleSingleFormChange}
                    placeholder="agent.username"
                    required
                  />
                  <small>1-100 characters (alphanumeric, dots, hyphens, underscores)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={singleForm.email}
                    onChange={handleSingleFormChange}
                    placeholder="agent@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={singleForm.first_name}
                    onChange={handleSingleFormChange}
                    placeholder="John"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Last Name *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={singleForm.last_name}
                    onChange={handleSingleFormChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Phone Configuration</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone_type">Phone Type *</label>
                  <select
                    id="phone_type"
                    name="phone_type"
                    value={singleForm.phone_type}
                    onChange={handleSingleFormChange}
                    required
                  >
                    <option value="SOFT_PHONE">Soft Phone</option>
                    {!isStandardUser && <option value="DESK_PHONE">Desk Phone</option>}
                  </select>
                </div>
                {singleForm.phone_type === 'DESK_PHONE' && (
                  <div className="form-group">
                    <label htmlFor="desk_phone_number">Desk Phone Number *</label>
                    <input
                      type="text"
                      id="desk_phone_number"
                      name="desk_phone_number"
                      value={singleForm.desk_phone_number}
                      onChange={handleSingleFormChange}
                      placeholder="+12125551234"
                      required={singleForm.phone_type === 'DESK_PHONE'}
                    />
                    <small>E.164 format (e.g., +12125551234)</small>
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>Profiles and Security</h3>
              <div className="form-group">
                <label htmlFor="routing_profile_id">Routing Profile *</label>
                <select
                  id="routing_profile_id"
                  name="routing_profile_id"
                  value={singleForm.routing_profile_id}
                  onChange={handleSingleFormChange}
                  required
                  disabled={loadingRoutingProfiles || !singleForm.instance_id}
                >
                  <option value="">
                    {!singleForm.instance_id 
                      ? 'Select an instance first...' 
                      : loadingRoutingProfiles 
                      ? 'Loading routing profiles...' 
                      : 'Select a routing profile...'}
                  </option>
                  {routingProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
                <small>Select the routing profile for this agent</small>
              </div>

              <div className="form-group">
                <label htmlFor="security_profile_ids">Security Profiles *</label>
                <select
                  id="security_profile_ids"
                  name="security_profile_ids"
                  value={singleForm.security_profile_ids}
                  onChange={handleSingleFormChange}
                  required
                  multiple
                  disabled={loadingSecurityProfiles || !singleForm.instance_id}
                  style={{ minHeight: '120px' }}
                >
                  {!singleForm.instance_id ? (
                    <option value="" disabled>Select an instance first...</option>
                  ) : loadingSecurityProfiles ? (
                    <option value="" disabled>Loading security profiles...</option>
                  ) : securityProfiles.length === 0 ? (
                    <option value="" disabled>No security profiles available</option>
                  ) : (
                    securityProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))
                  )}
                </select>
                <small>Hold Ctrl (Cmd on Mac) to select multiple security profiles</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating Agent...' : 'Create Agent'}
              </button>
            </div>
          </form>
        </div>
      )}

      {mode === 'bulk' && (
        <div className="form-container">
          <div className="info-box">
            <h3>📋 CSV Format Requirements</h3>
            <p>Your CSV file must have exactly <strong>43 columns</strong> in this order:</p>
            <div style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
              <p><strong>Mandatory fields (8):</strong></p>
              <ol style={{ marginLeft: '20px', paddingLeft: '0' }}>
                <li>first_name</li>
                <li>last_name</li>
                <li>email (valid email format)</li>
                <li>mobile (E.164 format, e.g., +15555551234)</li>
                <li>user_login (alphanumeric, dots, hyphens, underscores)</li>
                <li>routing_profile_name</li>
                <li>security_profile_1 (at least one security profile required)</li>
                <li>phone_type (SOFT_PHONE or DESK_PHONE)</li>
              </ol>
              
              <p className="mt-2"><strong>Optional fields (35):</strong></p>
              <ul style={{ marginLeft: '20px', paddingLeft: '0', listStyleType: 'disc' }}>
                <li>hierarchy_group_name</li>
                <li>security_profile_2, security_profile_3, security_profile_4, security_profile_5</li>
                <li>phone_number (required if phone_type is DESK_PHONE)</li>
                <li>soft_phone_auto_accept (true/false)</li>
                <li>acw_timeout (seconds)</li>
                <li>persistent_connection (true/false)</li>
                <li><strong>Proficiencies (15 columns):</strong> proficiency_name_1, proficiency_value_1, proficiency_level_1 through proficiency_name_5, proficiency_value_5, proficiency_level_5 (all three fields required together, values and levels must be 1-10)</li>
                <li>tag_key1 through tag_key5 (with corresponding values)</li>
                <li>tag_value1 through tag_value5</li>
                <li>agent_group — DCI agent group name to assign the agent to (must match an existing group name; leave blank to leave unassigned)</li>
              </ul>
            </div>
            
            <p className="mt-2" style={{ fontSize: '0.9rem' }}>
              <strong>⚠️ Important Notes:</strong>
            </p>
            <ul style={{ marginLeft: '20px', paddingLeft: '0', fontSize: '0.85rem', listStyleType: 'disc' }}>
              <li>Proficiency name/value/level must be provided as triplets (all three fields together or all empty)</li>
              <li>Tag key/value must be provided as pairs</li>
              <li>Proficiency values and levels must be between 1 and 10</li>
              <li>Phone numbers must be in E.164 format (+[country code][number])</li>
              <li>Use profile/group NAMES, not IDs</li>
            </ul>
            
            <div className="mt-2" style={{ textAlign: 'center', padding: '1rem 0', borderTop: '1px solid #e0e0e0' }}>
              <button 
                type="button"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${process.env.PUBLIC_URL}/sample_bulk_agent_upload.csv`;
                  link.download = 'sample_bulk_agent_upload.csv';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="btn-download-sample"
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#1976D2';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#2196F3';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>📥</span>
                Download Sample CSV
              </button>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                Download a sample CSV file with 5 example agent records
              </small>
            </div>
          </div>

          <form onSubmit={handleBulkSubmit} className="setup-form">
            <div className="form-section">
              <h3>Instance Information</h3>
              <div className="form-group">
                <label htmlFor="bulk_instance_id">Instance *</label>
                <select
                  id="bulk_instance_id"
                  name="instance_id"
                  value={bulkForm.instance_id}
                  onChange={handleBulkFormChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
              </div>
            </div>

            <div className="form-section">
              <h3>CSV File Upload</h3>
              <div className="form-group">
                <label htmlFor="csv_file">CSV File *</label>
                <input
                  type="file"
                  id="csv_file"
                  name="csv_file"
                  accept=".csv"
                  onChange={handleFileChange}
                  required
                />
                {csvFileName && (
                  <small className="file-selected">Selected: {csvFileName}</small>
                )}
                <small>Upload a CSV file with agent information</small>
              </div>
            </div>

            {csvValidationErrors.length > 0 && (
              <div className="validation-errors">
                <h4>Validation Errors ({csvValidationErrors.length}):</h4>
                <div className="error-list">
                  {csvValidationErrors.slice(0, 50).map((error, index) => (
                    <div key={index} className="error-item">
                      {error.row && <span className="error-row">Row {error.row}: </span>}
                      {error.field && <span className="error-field">{error.field} - </span>}
                      <span className="error-message">{error.error || error.message}</span>
                    </div>
                  ))}
                  {csvValidationErrors.length > 50 && (
                    <p className="error-truncated">
                      ... and {csvValidationErrors.length - 50} more errors
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || !bulkForm.csv_file}>
                {loading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </div>
          </form>
        </div>
      )}

      {mode === 'bulk-update' && (
        <div className="form-container">
          <div className="info-box">
            <h3>✏️ Bulk Update — How it works</h3>
            <div style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
              <p>Use the <strong>18-column update template</strong> (proficiency and tag columns are excluded). Only three fields are required per row — all others are <strong>optional</strong>.</p>
              <p className="mt-2"><strong>Required (3):</strong></p>
              <ol style={{ marginLeft: '20px', paddingLeft: '0' }}>
                <li>user_login — identifies the agent to update</li>
                <li>first_name</li>
                <li>last_name</li>
              </ol>
              <p className="mt-2"><strong>Optional — leave blank to keep existing value:</strong></p>
              <ul style={{ marginLeft: '20px', paddingLeft: '0', listStyleType: 'disc' }}>
                <li><strong>routing_profile_name</strong> — replaces current routing profile</li>
                <li><strong>hierarchy_group_name</strong> — supports full pipe-separated path (e.g. <code>Region|Sales|Team A</code>); replaces current hierarchy</li>
                <li><strong>security_profile_1..5</strong> — <em>additive</em>: new profiles are added to existing set, none are removed</li>
                <li><strong>phone_type / phone_number / soft_phone_auto_accept / acw_timeout / persistent_connection</strong> — any non-empty phone field triggers a phone config update; missing phone fields are preserved from current config</li>
                <li><strong>email / mobile</strong> — replaces current identity info fields if provided</li>
                <li><strong>agent_group</strong> — updates DCI group assignment if provided; empty = no DCI change</li>
                <li><strong>proficiency_* and tag_* columns</strong> — ignored (not updated)</li>
              </ul>
            </div>
            <div className="mt-2" style={{ textAlign: 'center', padding: '1rem 0', borderTop: '1px solid #e0e0e0' }}>
              <button
                type="button"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${process.env.PUBLIC_URL}/sample_bulk_agent_update.csv`;
                  link.download = 'sample_bulk_agent_update.csv';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="btn-download-sample"
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>📥</span>
                Download CSV Template
              </button>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                Download a 17-column update template (proficiency and tag columns excluded)
              </small>
            </div>
          </div>

          <form onSubmit={handleBulkUpdateSubmit} className="setup-form">
            <div className="form-section">
              <h3>Instance Information</h3>
              <div className="form-group">
                <label htmlFor="update_instance_id">Instance *</label>
                <select
                  id="update_instance_id"
                  name="instance_id"
                  value={updateForm.instance_id}
                  onChange={handleUpdateFormChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select the Amazon Connect instance containing the agents to update</small>
              </div>
            </div>

            <div className="form-section">
              <h3>CSV File Upload</h3>
              <div className="form-group">
                <label htmlFor="update_csv_file">CSV File *</label>
                <input
                  type="file"
                  id="update_csv_file"
                  name="csv_file"
                  accept=".csv"
                  onChange={handleUpdateFileChange}
                  required
                />
                {updateCsvFileName && (
                  <small className="file-selected">Selected: {updateCsvFileName}</small>
                )}
                <small>Upload a CSV file with the fields you want to update (17-column update template required)</small>
              </div>
            </div>

            {updateCsvValidationErrors.length > 0 && (
              <div className="validation-errors">
                <h4>Validation Errors ({updateCsvValidationErrors.length}):</h4>
                <div className="error-list">
                  {updateCsvValidationErrors.slice(0, 50).map((error, index) => (
                    <div key={index} className="error-item">
                      {error.row && <span className="error-row">Row {error.row}: </span>}
                      {error.field && <span className="error-field">{error.field} - </span>}
                      <span className="error-message">{error.error || error.message}</span>
                    </div>
                  ))}
                  {updateCsvValidationErrors.length > 50 && (
                    <p className="error-truncated">
                      ... and {updateCsvValidationErrors.length - 50} more errors
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || !updateForm.csv_file}>
                {loading ? 'Uploading...' : 'Upload Update CSV'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Processing your request...</p>
        </div>
      )}

      {/* Bulk job submitted confirmation */}
      {submittedJobId && (
        <div className="alert alert-success" style={{ marginTop: '1rem' }}>
          <h4>✅ Bulk Upload Submitted</h4>
          <p>
            Your CSV has been accepted and queued for processing.
          </p>
          <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.75rem' }}>
            Job ID: <code>{submittedJobId}</code>
          </p>
          <Link to="/bulk-jobs" className="btn btn-primary" style={{ display: 'inline-block' }}>
            View Job Status →
          </Link>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          {mode === 'single' ? (
            <div>
              <p><strong>Agent created successfully!</strong></p>
              <p><strong>User ID:</strong> {result.result?.UserId || 'N/A'}</p>
              <p><strong>Username:</strong> {singleForm.username}</p>
              {result.result?.UserArn && (
                <p><strong>User ARN:</strong> {result.result.UserArn}</p>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SetupAgentPage;
